import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import api from '../../services/api'
import ssoService from '../../utility/auth/ssoService'
import { buildAbility } from '../../utility/context/ability'

const SSO_ONLY = process.env.REACT_APP_SSO_ONLY === 'true'

// ── Token / session helpers ───────────────────────────────────────────────────

export const session = {
  token:    ()      => localStorage.getItem('frtm_token'),
  user:     ()      => { try { return JSON.parse(localStorage.getItem('frtm_user')) } catch { return null } },
  set:      (token, user) => {
    localStorage.setItem('frtm_token', token)
    localStorage.setItem('frtm_user',  JSON.stringify(user))
  },
  clear:    ()      => {
    localStorage.removeItem('frtm_token')
    localStorage.removeItem('frtm_user')
  },
}

// ── Role → home route ─────────────────────────────────────────────────────────

export const homeRouteForRole = (role) => {
  const map = {
    RISK_ADMIN:   '/dashboard',
    RISK_ANALYST: '/alerts',
    OPERATIONS:   '/merchants',
    VIEWER:       '/dashboard',
    SUPER_ADMIN:  '/dashboard',
  }
  return map[role] || '/dashboard'
}

// ── Thunks ────────────────────────────────────────────────────────────────────

/** Dev: email + password login (disabled when SSO_ONLY=true) */
export const loginWithCredentials = createAsyncThunk(
  'auth/loginWithCredentials',
  async ({ email, password }, { rejectWithValue }) => {
    if (SSO_ONLY) return rejectWithValue('Local login disabled. Use SSO.')
    try {
      const { access_token } = await api
        .post('/api/v1/auth/login', { email, password })
        .then(r => r.data)

      const me = await api
        .get('/api/v1/auth/me', { headers: { Authorization: `Bearer ${access_token}` } })
        .then(r => r.data)

      const user = { ...me, access_token }
      session.set(access_token, user)
      return user
    } catch (e) {
      return rejectWithValue(e.response?.data?.detail || e.message)
    }
  }
)

/**
 * Prod: Entra SSO — called from SsoCallback after Entra redirects with ?code=
 * ssoService.exchangeCode() hits backend /auth/sso/callback which calls Entra's
 * /token endpoint and returns the Entra access_token.
 */
export const loginWithSso = createAsyncThunk(
  'auth/loginWithSso',
  async ({ token }, { rejectWithValue }) => {
    try {
      const data = await api
        .post('/api/v1/auth/sso-login', { token })
        .then(r => r.data)

      const user = {
        id:    data.user.id,
        email: data.user.email,
        name:  data.user.name,
        role:  data.user.role,
        roles: data.user.roles,
      }

      session.set(data.access_token, user)
      return { user, returnTo: null }
    } catch (e) {
      return rejectWithValue(
        e.response?.data?.detail || e.message || 'SSO login failed.'
      )
    }
  }
)

/** Restore session from localStorage on app load */
export const restoreSession = createAsyncThunk(
  'auth/restoreSession',
  async (_, { rejectWithValue }) => {
    const token = session.token()
    const user  = session.user()
    if (!token || !user) return rejectWithValue('No session')

    // Verify token is still valid against the backend
    try {
      const me = await api
        .get('/api/v1/auth/me', { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.data)
      return { ...user, ...me }
    } catch {
      session.clear()
      return rejectWithValue('Session expired')
    }
  }
)

export const logout = createAsyncThunk('auth/logout', async () => {
  // ALWAYS clear local session first — regardless of SSO or credentials login.
  // Without this, the localStorage token survives the SSO redirect and
  // restoreSession() auto-logs the user back in on the next page load.
  session.clear()

  if (process.env.REACT_APP_SSO_PROVIDER === 'entra') {
    // Also sign out of Entra so the SSO cookie is cleared.
    // The redirect happens AFTER localStorage is already cleared above.
    try {
      await ssoService.logout()   // redirects to Entra logout page
    } catch {
      // If Entra redirect fails, local session is already cleared — user is logged out locally.
      window.location.href = '/login'
    }
  }
})


// ── Slice ─────────────────────────────────────────────────────────────────────

const authSlice = createSlice({
  name: 'auth',
  initialState: {
    user:         null,
    token:        session.token() || null,
    loading:      false,
    error:        null,
    initialised:  false,
  },
  reducers: {
    clearError(state) { state.error = null },
    // Sync action — clears auth state immediately without waiting for async thunk
    // Use this when you need instant state clear (e.g. before SSO redirect)
    clearAuth(state) {
      state.user  = null
      state.token = null
      state.error = null
    },
  },
  extraReducers: (b) => {
    // loginWithCredentials
    b.addCase(loginWithCredentials.pending,   (s)    => { s.loading = true;  s.error = null })
    b.addCase(loginWithCredentials.rejected,  (s, a) => { s.loading = false; s.error = a.payload })
    b.addCase(loginWithCredentials.fulfilled, (s, a) => {
      s.loading = false
      s.user    = a.payload
      s.token   = a.payload.access_token
    })

    // loginWithSso
    b.addCase(loginWithSso.pending,   (s)    => { s.loading = true;  s.error = null })
    b.addCase(loginWithSso.rejected,  (s, a) => { s.loading = false; s.error = a.payload })
    b.addCase(loginWithSso.fulfilled, (s, a) => {
      s.loading = false
      s.user    = a.payload.user
      s.token   = session.token()
    })

    // restoreSession
    b.addCase(restoreSession.pending,   (s)    => { s.loading = true })
    b.addCase(restoreSession.rejected,  (s)    => { s.loading = false; s.initialised = true; s.user = null; s.token = null })
    b.addCase(restoreSession.fulfilled, (s, a) => { s.loading = false; s.initialised = true; s.user = a.payload; s.token = session.token() })

    // logout
    b.addCase(logout.fulfilled, (s) => { s.user = null; s.token = null })
  },
})

export const { clearError, clearAuth } = authSlice.actions

// ── Selectors ─────────────────────────────────────────────────────────────────
export const selectUser        = s => s.auth.user
export const selectAuthLoading = s => s.auth.loading
export const selectAuthError   = s => s.auth.error
export const selectIsAuth      = s => !!s.auth.token
export const selectInitialised = s => s.auth.initialised

// ── Compatibility aliases — used by router, guards, App.js ────────────────────
export const selectIsAuthenticated = selectIsAuth
export const selectIsInitialising  = s => !s.auth.initialised
export const initAuth              = restoreSession

// selectAbility — returns a real CASL Ability instance built from the user's role.
// Memoised by role string so useSelector doesn't trigger infinite re-renders
// (a new object reference on every call would cause AbilityContext to re-render forever).


let _cachedRole    = null
let _cachedAbility = null

export const selectAbility = s => {
  const role = s.auth.user?.role ?? 'VIEWER'
  // Return the same object reference if role hasn't changed
  if (role === _cachedRole && _cachedAbility) return _cachedAbility
  _cachedRole    = role
  _cachedAbility = buildAbility(role)
  return _cachedAbility
}

export default authSlice.reducer